import { useState } from "react";
import { isEqual } from "lodash";
import { Flex } from "styles/utility-components";

import { convertNoticeFiltersToView, EMPTY_FILTERS } from "components/notices/utils";
import { DeleteViewModal } from "components/saved_searches/DeleteViewModal";
import { DuplicateViewModal } from "components/saved_searches/DuplicateViewModal";
import { EditAlertsModal } from "components/saved_searches/EditAlertsModal";
import { EditViewModal } from "components/saved_searches/EditViewModal";
import { ModalView, viewActionTooltip } from "components/saved_searches/utils";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import Popover from "components/ui/popover/Popover";
import toast from "components/ui/toast/Toast";
import SavedViewsMenu, { BasicView } from "components/views/SavedViewsMenu";
import { useSavedViewAlert } from "lib/hooks/api/lead_subscriptions/useSavedViewAlert";
import { useAllViews } from "lib/hooks/api/notices/views/useAllViews";
import { usePinView } from "lib/hooks/api/notices/views/usePinView";
import { useUpdateSavedView } from "lib/hooks/api/saved_views/useUpdateSavedView";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { createEditAlertsModalOpened } from "lib/savedViewTracking";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { SavedViewDataType } from "lib/types/graphQLEnums";
import { SavedViewType } from "lib/types/savedViews";
import { assert } from "lib/utils";
import { useUnpinView } from "../../../lib/hooks/api/notices/views/useUnpinView";
import { useNoticeListsPageState, useSavedViews } from "../useNoticeListsPageState";
import { CreateViewModal } from "./CreateViewModal";

type ViewOptionsProps = {
  selectedView?: SavedViewType;
  pinnedViews: SavedViewType[];
};

function ViewOptions({ selectedView, pinnedViews }: ViewOptionsProps) {
  const { logEvent } = useTracking();
  const { data: allViews } = useAllViews(SavedViewDataType.Notice);
  const { filters, hasUserModifiedView, tableColumns, setSelectedView } = useNoticeListsPageState();
  const { recentViews } = useSavedViews();
  const hydratedRecentViews = recentViews.map((id: string) => allViews?.find((v) => v.id === id));
  const { alert: personalSavedViewAlert } = useSavedViewAlert(selectedView?.id);
  const { data: signals, isLoading: isLoadingSignals } = useSignalSettings();

  // Helper function to transform view objects into the expected format
  const transformViewToBasicView = (view: SavedViewType): BasicView => ({
    id: view.id ?? "",
    name: view.name ?? "",
    description: view.description ?? "",
    dataType: view.dataType,
  });

  const [isEditAlertsModalOpen, setIsEditAlertsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isPinned = !!(selectedView && pinnedViews?.some((view) => view.id === selectedView.id));

  const hasAlertsEnabled = Boolean(selectedView?.id && personalSavedViewAlert);

  // The saved_searches modals take a narrower view shape with a required string id.
  const modalView: ModalView | undefined = selectedView?.id
    ? { ...selectedView, id: selectedView.id }
    : undefined;

  const { mutate: updateSavedView } = useUpdateSavedView("Update view dropdown", {
    onSuccess: (_data, vars) => {
      toast.success(`Successfully updated ${vars.request.name}`);
      setSelectedView(vars.request.id, vars.request.name ?? "", "Update view");
    },
    onError: (_data, vars) => toast.error(`Failed to update ${vars.request.name}`),
  });

  const { mutate: pinView } = usePinView({
    onSuccess: async () => {
      toast.success("Search pinned successfully!");
    },
    onError: async () => {
      toast.error("Failed to pin search");
    },
  });

  const { mutate: unpinView } = useUnpinView({
    onSuccess: async () => {
      toast.success("Search unpinned successfully!");
    },
    onError: async () => {
      toast.error("Failed to unpin search");
    },
  });

  const user = useAssertCurrentUser();

  const filtersEmpty = isEqual(
    JSON.parse(JSON.stringify(filters)),
    JSON.parse(JSON.stringify(EMPTY_FILTERS)),
  );

  return (
    <Flex gap="8px" justifyContent="right">
      <CreateViewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        signals={signals}
        dataType="notice"
      />
      <Popover trigger={<DropdownMenuTrigger>Saved searches</DropdownMenuTrigger>} hideArrow>
        <SavedViewsMenu
          dataType="notice"
          pinnedViews={pinnedViews.map(transformViewToBasicView)}
          recentViews={hydratedRecentViews
            .filter((v) => v !== undefined)
            .map(transformViewToBasicView)}
          templates={allViews?.filter((v) => v.isStandardView).map(transformViewToBasicView)}
          setSelectedView={setSelectedView}
        />
      </Popover>

      <DropdownMenu
        trigger={<DropdownMenuTrigger buttonType="primary">Save</DropdownMenuTrigger>}
        width="fit-content"
        modal={false}
      >
        <DropdownMenuItem
          key="update-current-view"
          icon="save01"
          disabled={
            !selectedView ||
            selectedView.isStandardView ||
            !hasUserModifiedView ||
            (selectedView.owner?.guid !== user.guid && !user.isAdmin)
          }
          loading={isLoadingSignals}
          onClick={() => {
            assert(selectedView);
            if (!selectedView.id) return;
            const convertedViewToFilter = convertNoticeFiltersToView(
              filters,
              tableColumns,
              signals,
            );
            updateSavedView({
              request: {
                id: selectedView.id,
                name: selectedView.name,
                view: JSON.stringify(convertedViewToFilter),
              },
            });
          }}
        >
          Save changes
        </DropdownMenuItem>
        <DropdownMenuItem
          key="save-as-new-view"
          icon="save01"
          disabled={!hasUserModifiedView || filtersEmpty}
          loading={isLoadingSignals}
          onClick={() => {
            setIsCreateModalOpen(true);
          }}
        >
          Save as new search
        </DropdownMenuItem>
      </DropdownMenu>
      {modalView && (
        <>
          <EditAlertsModal
            isOpen={isEditAlertsModalOpen}
            onOpenChange={setIsEditAlertsModalOpen}
            selectedView={modalView}
          />
          <EditViewModal
            isOpen={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            selectedView={modalView}
          />
          <DuplicateViewModal
            isOpen={isDuplicateModalOpen}
            onOpenChange={(open, newViewId) => {
              setIsDuplicateModalOpen(open);
              if (newViewId) setSelectedView(newViewId, "", "Duplicate view modal");
            }}
            selectedView={modalView}
          />
          <DeleteViewModal
            isOpen={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
            selectedView={modalView}
            onSuccess={() => setSelectedView(undefined, "", "Delete view modal")}
          />
        </>
      )}

      <DropdownMenu
        trigger={
          <DropdownMenuTrigger
            buttonType="secondary"
            hideArrow
            icon="dotsHorizontal"
            disabled={!selectedView}
            aria-label="More options"
          />
        }
        width="fit-content"
        modal={false}
      >
        {selectedView ? (
          <>
            <DropdownMenuItem
              key="set-alerts"
              icon="bell01"
              iconProps={{
                fill: hasAlertsEnabled ? "sysTextDefault" : "transparent",
              }}
              onClick={() => {
                logEvent(
                  EventNames.alertModalOpened,
                  createEditAlertsModalOpened(
                    selectedView.name,
                    EventDataTypes.notices,
                    selectedView.id,
                  ),
                );
                setIsEditAlertsModalOpen(true);
              }}
            >
              Set alerts
            </DropdownMenuItem>
            <DropdownMenuItem
              key="unpin"
              icon="pin02"
              iconProps={{
                fill: isPinned ? "currentColor" : "transparent",
              }}
              onClick={() => {
                assert(selectedView.id);
                isPinned
                  ? unpinView({
                      id: selectedView.id,
                      name: selectedView.name,
                    })
                  : pinView({ id: selectedView.id, name: selectedView.name });
              }}
            >
              {isPinned ? "Unpin search" : "Pin search"}
            </DropdownMenuItem>
            <DropdownMenuItem
              key="duplicate"
              icon="copy07"
              onClick={() => {
                setIsDuplicateModalOpen(true);
              }}
            >
              Duplicate search
            </DropdownMenuItem>
            <DropdownMenuItem
              key="edit"
              icon="edit05"
              onClick={() => {
                setIsEditModalOpen(true);
              }}
              disabled={selectedView.isStandardView || selectedView.owner?.guid !== user.guid}
              tooltip={viewActionTooltip(
                "edit",
                {
                  isStandardView: selectedView.isStandardView,
                  owner: selectedView.owner ?? null,
                },
                user.guid,
              )}
            >
              Edit search
            </DropdownMenuItem>
            <DropdownMenuItem
              key="delete"
              icon="trash01"
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
              disabled={selectedView.isStandardView || selectedView.owner?.guid !== user.guid}
              tooltip={viewActionTooltip(
                "delete",
                {
                  isStandardView: selectedView.isStandardView,
                  owner: selectedView.owner ?? null,
                },
                user.guid,
              )}
            >
              Delete search
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem disabled>No view selected</DropdownMenuItem>
        )}
      </DropdownMenu>
    </Flex>
  );
}

export default ViewOptions;
